import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { PageHeading } from '@/components/page-heading'
import { CurrentShowSummary, SelectShowNotice } from '@/components/current-show'
import { Card, CardHeader, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useCurrentShow } from '@/context/current-show'
import { useRunMode } from '@/context/run-mode'
import {
  useEntryIntakeManager,
  type PendingIntakeEntry,
  type CheckedInExhibit,
} from '@/hooks/useEntryIntake'
import { useActorPermissions, formatRoleList } from '@/hooks/useActorPermissions'
import { useAllocateEntryCodes, useCreateEntry } from '@/api/hooks'
import { usePendingEntrySync } from '@/hooks/usePendingEntrySync'
import { syncsWithAll } from '@/hooks/pending-entry-sync'
import { PendingEntriesSection } from '@/components/intake/PendingEntriesSection'
import { RecentCheckIns } from '@/components/entries/RecentCheckIns'
import { useLabelPrinter, useLabelPrinterDiscovery } from '@/context/label-printer'
import type { TEntry } from '@schemas'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'
import { showAllowsLabelPrinting } from '@/lib/label/permissions'
import { LabelPrintingDisabledNotice } from '@/components/label-printing-disabled-notice'

export default function StatusPage() {
  const { currentShow } = useCurrentShow()
  const { mode } = useRunMode()
  const navigate = useNavigate()
  const currentSlug = useShowSlug()
  const intake = useEntryIntakeManager(currentShow?.id)
  const allocateCodes = useAllocateEntryCodes()
  const createEntry = useCreateEntry()
  const { getJobStatus } = useLabelPrinter()
  const labelPrintingDisabled = currentShow?.labelPrintingDisabled ?? false
  const allowsLabelPrinting = showAllowsLabelPrinting(currentShow ?? null)
  useLabelPrinterDiscovery(allowsLabelPrinting)
  const permissions = useActorPermissions(currentShow?.id)
  const [replacingEntry, setReplacingEntry] = useState<PendingIntakeEntry | null>(null)
  const [replacementAcknowledged, setReplacementAcknowledged] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [showAllCodes, setShowAllCodes] = useState(false)
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  )
  const previousStatusRef = useRef<string | null>(null)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }, [])

  useEffect(() => {
    const previousStatus = previousStatusRef.current
    const currentStatus = currentShow?.status ?? null
    if (previousStatus === 'accepting_entries' && currentStatus !== 'accepting_entries') {
      toast.error('This show is no longer accepting entries. Intake actions are disabled.')
    }
    previousStatusRef.current = currentStatus
  }, [currentShow?.status])

  const permissionLoading = permissions.staffQuery?.isLoading ?? false
  const canManageEntries = permissions.can('entries:write')
  const canAllocateCodes = permissions.can('entrycodes:allocate')
  const showAllowsIntake = currentShow?.status === 'accepting_entries'

  const entriesRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('entries:write')),
    [permissions]
  )
  const allocateRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('entrycodes:allocate')),
    [permissions]
  )

  const entryPermissionDisabled = permissionLoading || !canManageEntries
  const entryPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canManageEntries
      ? `Creating entries requires ${entriesRolesLabel}.`
      : undefined
  const allocationPermissionDisabled = permissionLoading || !canAllocateCodes
  const allocationPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canAllocateCodes
      ? `Fetching Exhibit IDs requires ${allocateRolesLabel}.`
      : undefined

  const returnableCode = intake.lastClaimedCode
  const nextExhibitId = intake.availableCodes.length > 0 ? intake.availableCodes[0] : null
  const remainingCodes =
    intake.availableCodes.length > 1 ? intake.availableCodes.slice(1) : ([] as string[])
  const hiddenCodeCount = showAllCodes ? 0 : remainingCodes.length
  const networkDisabledReason = !isOnline ? 'Unavailable while offline' : undefined
  const [syncDisabledReason, syncDisabledBase] = useMemo(() => {
    if (entryPermissionDisabled) {
      return [entryPermissionReason, true] as const
    }
    if (!isOnline) {
      return ['Unavailable while offline', true] as const
    }
    if (createEntry.isPending) {
      return ['Entry submission in progress', true] as const
    }
    if (allocateCodes.isPending) {
      return ['Code allocation in progress', true] as const
    }
    return [undefined, false] as const
  }, [
    allocateCodes.isPending,
    createEntry.isPending,
    entryPermissionDisabled,
    entryPermissionReason,
    isOnline,
  ])

  const handleEntrySyncSuccess = useCallback(
    async (
      entry: TEntry,
      options: {
        usedCode: string
        originalCode?: string | null
        className?: string | null
        classId?: string
      }
    ) => {
      await intake.clearLastClaimed()
      const canonicalCode = entry.entryCode ?? options.usedCode
      const originalPersisted =
        options.originalCode && options.originalCode !== canonicalCode
          ? options.originalCode
          : options.usedCode.startsWith('TMP') && options.usedCode !== canonicalCode
            ? options.usedCode
            : undefined
      const exhibitorId = entry.exhibitorId ?? null
      if (exhibitorId) {
        await intake.recordRecentEntry({
          id: entry.id ?? `${exhibitorId}:${canonicalCode}`,
          entryCode: canonicalCode,
          exhibitorId,
          className: options.className ?? null,
          originalCode: originalPersisted ?? null,
        })
      }
      const mapping =
        originalPersisted && originalPersisted !== canonicalCode
          ? `${originalPersisted} → ${canonicalCode}`
          : null
      toast.success(
        mapping ? `Entry ${canonicalCode} created (${mapping})` : `Entry ${canonicalCode} created`
      )
    },
    [intake]
  )

  const { syncPendingEntries, isSyncing } = usePendingEntrySync({
    intake,
    currentShowId: currentShow?.id ?? null,
    currentShowStatus: currentShow?.status ?? null,
    isOnline,
    entryPermissionDisabled,
    entryPermissionReason,
    allocationPermissionDisabled,
    allocationPermissionReason,
    createEntry,
    allocateCodes,
    onEntryCreated: (entry, info) =>
      handleEntrySyncSuccess(entry, {
        usedCode: info.usedCode,
        originalCode: info.originalCode,
        className: info.className,
        classId: info.classId,
      }),
  })

  const syncDisabled = syncDisabledBase || isSyncing
  const fetchDisabledReason = allocationPermissionDisabled
    ? allocationPermissionReason
    : networkDisabledReason
  const hasPendingEntries = intake.pendingEntries.length > 0
  const showSyncNowPrompt = isOnline && hasPendingEntries

  const handleReturnLastCode = async () => {
    if (!returnableCode) return
    if (entryPermissionDisabled) {
      toast.error(entryPermissionReason ?? 'Returning Exhibit IDs requires additional permissions.')
      return
    }
    const code = returnableCode
    await intake.returnLastClaimed()
    toast.success(`Returned ${code} to the Exhibit ID pool`)
  }

  const handleCopyExhibitId = useCallback(
    async (entryCode?: string | null) => {
      if (!showAllowsIntake) {
        toast.error('Copying Exhibit IDs is unavailable once intake closes.')
        return
      }
      if (!entryCode) {
        toast.error('Exhibit ID unavailable.')
        return
      }
      if (typeof navigator === 'undefined') {
        toast.error('Copy is unavailable in this environment.')
        return
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(entryCode)
          toast.success('Exhibit ID copied')
        } else {
          throw new Error('Clipboard API unavailable')
        }
      } catch {
        toast.error('Unable to copy Exhibit ID. Copy it manually.')
      }
    },
    [showAllowsIntake]
  )

  const allocateAndStoreCodes = useCallback(
    async (count: number) => {
      if (!currentShow?.id) return
      if (allocationPermissionDisabled) {
        toast.error(
          allocationPermissionReason ?? 'Fetching Exhibit IDs requires additional permissions.'
        )
        return
      }
      if (!isOnline) {
        toast.error('Cannot fetch Exhibit IDs while offline')
        return
      }
      try {
        const { codes, identifiers } = await allocateCodes.mutateAsync({
          showId: currentShow.id,
          count,
        })
        await intake.pushCodes(codes, identifiers)
        toast.success(`Fetched ${codes.length} Exhibit IDs`)
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        } else {
          toast.error('Unable to allocate Exhibit IDs')
        }
      }
    },
    [
      allocationPermissionDisabled,
      allocationPermissionReason,
      allocateCodes,
      currentShow?.id,
      intake,
      isOnline,
    ]
  )

  const handlePendingNotesUpdate = async (entry: PendingIntakeEntry, value: string) => {
    try {
      const saved = await intake.updatePending(
        entry.id,
        { notes: value },
        { notes: entry.notes ?? '' }
      )
      const stored = saved.pendingEntries.find(item => item.id === entry.id)
      if (!stored) {
        toast.error('This entry is no longer pending. Your notes were not saved.')
        return false
      }
      if ((stored.notes ?? '') !== value) {
        toast.error('These notes were changed in another tab. Your notes were not saved.')
        return false
      }
      return true
    } catch {
      toast.error('Unable to save notes on this device.')
      return false
    }
  }

  const handleRemovePending = async (entry: PendingIntakeEntry) => {
    // A queued code may already be printed or accepted by the server. Retire it.
    try {
      await intake.removePending(entry.id)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Unable to remove the pending entry from this device.'
      )
    }
  }

  const handleSyncPending = () => {
    void syncPendingEntries(
      intake.pendingEntries.filter(entry => syncsWithAll(entry, showAllowsIntake))
    )
  }

  const handleSyncSingle = async (entry: PendingIntakeEntry) => {
    // Trying a duplicate again is staff's call, after the other entry is marked Not Entered.
    if (entry.status === 'duplicate') {
      try {
        await intake.retryDuplicate(entry.id)
      } catch {
        toast.error('Unable to update the pending entry on this device.')
        return
      }
    }
    void syncPendingEntries([entry])
  }

  const getRecentJobStatus = useCallback(
    (entryId: string) => {
      const job = getJobStatus(entryId)
      if (!job) return undefined
      if (job.status === 'pending' || job.status === 'success' || job.status === 'error') {
        return { status: job.status, errorMessage: job.errorMessage ?? undefined }
      }
      return undefined
    },
    [getJobStatus]
  )

  const handleRecentCopy = useCallback(
    (entryCode: string | null | undefined) => {
      if (!entryCode) return
      void handleCopyExhibitId(entryCode)
    },
    [handleCopyExhibitId]
  )

  const handleOpenRecentEntry = useCallback(
    (event: CheckedInExhibit) => {
      if (!event.exhibitorId) {
        toast.error('Entry details unavailable. Refresh and try again.')
        return
      }
      navigate(showAware('/check-in', { currentSlug }), {
        state: { exhibitorId: event.exhibitorId },
      })
    },
    [currentSlug, navigate]
  )

  const guardContent =
    mode === 'run' && !currentShow ? (
      <SelectShowNotice
        title="Select a show to view status"
        description="Pick a show and move it to Accepting entries before using Run mode features."
        actionLabel="Choose show"
      />
    ) : !currentShow ? (
      <SelectShowNotice description="Choose a show to review intake status." />
    ) : null

  if (guardContent) {
    return guardContent
  }

  if (!currentShow) {
    return null
  }

  return (
    <div className="space-y-6">
      {intake.persistenceError && (
        <p role="alert" className="rounded border border-destructive p-3 text-destructive">
          {intake.persistenceError}
        </p>
      )}
      <header className="space-y-2">
        <PageHeading>Status</PageHeading>
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span>{currentShow.name}</span>
          <span>{isOnline ? 'Online' : 'Offline'}</span>
        </p>
      </header>

      {mode !== 'run' && <CurrentShowSummary show={currentShow} />}

      {labelPrintingDisabled && <LabelPrintingDisabledNotice />}

      {!isOnline && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">Offline mode enabled</p>
          <p className="mt-1">
            Sync actions and Exhibit ID fetches are disabled while offline. Reconnect to continue.
          </p>
        </div>
      )}

      {(showAllowsIntake || hasPendingEntries) && (
        <PendingEntriesSection
          entries={intake.pendingEntries}
          showAllowsIntake={showAllowsIntake}
          syncDisabled={syncDisabled}
          syncDisabledReason={syncDisabledReason}
          canReplaceCode={permissions.can('staff:write')}
          onSyncAll={handleSyncPending}
          onSync={handleSyncSingle}
          onRemove={handleRemovePending}
          onReplace={entry => {
            setReplacingEntry(entry)
            setReplacementAcknowledged(false)
          }}
          onReview={id => navigate(showAware(`/entries/${id}`, { currentSlug }))}
          onSaveNotes={handlePendingNotesUpdate}
        />
      )}

      {showAllowsIntake && (
        <details className="min-w-0 border-y py-4">
          <summary className="cursor-pointer text-base font-semibold">
            Exhibit ID Pool{' '}
            <span className="font-normal text-muted-foreground">
              · {intake.availableCodes.length} available
            </span>
          </summary>
          <Card className="mt-4 border-0 shadow-none py-0">
            <CardHeader className="px-0">
              <CardDescription>
                Exhibit IDs fetched in advance stay here for quick intake.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 px-0">
              {!permissionLoading && !canAllocateCodes && (
                <div className="rounded-md border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  Fetching Exhibit IDs requires {allocateRolesLabel}.
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Available Exhibit IDs</p>
                <Badge variant={intake.availableCodes.length > 0 ? 'default' : 'outline'}>
                  {intake.availableCodes.length}
                </Badge>
              </div>

              {returnableCode && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <span>
                    Last reserved Exhibit ID{' '}
                    <span className="font-mono font-semibold">{returnableCode}</span> is awaiting
                    return.
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleReturnLastCode}
                    disabled={entryPermissionDisabled}
                    title={entryPermissionReason}
                  >
                    Return last Exhibit ID
                  </Button>
                </div>
              )}

              {nextExhibitId ? (
                <div className="space-y-2 rounded-md border border-primary/30 bg-muted px-4 py-3">
                  <span className="text-xs font-semibold uppercase text-muted-foreground">
                    First Exhibit ID in pool
                  </span>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span
                      data-testid="next-exhibit-id-value"
                      className="font-mono text-4xl font-semibold tracking-wider"
                    >
                      {nextExhibitId}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="copy-next-exhibit-id"
                      onClick={() => handleCopyExhibitId(nextExhibitId)}
                    >
                      Copy Exhibit ID
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Check-in assigns the first ID that fits each entrant’s allocation order, so an
                    entry may receive a later ID or a temporary code.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No Exhibit IDs yet.</p>
              )}

              {remainingCodes.length > 0 && (
                <div className="space-y-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2"
                    data-testid="exhibit-id-list-toggle"
                    onClick={() => setShowAllCodes(current => !current)}
                  >
                    {showAllCodes
                      ? 'Hide Exhibit ID list'
                      : `Show Exhibit ID list (${hiddenCodeCount})`}
                  </Button>
                  {showAllCodes && (
                    <ul
                      data-testid="exhibit-id-list"
                      className="grid grid-cols-2 gap-2 sm:grid-cols-3"
                    >
                      {remainingCodes.map(code => (
                        <li key={code}>
                          <Badge
                            className="w-full justify-center font-mono text-sm"
                            variant="secondary"
                          >
                            {code}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
            <CardFooter className="flex flex-wrap gap-2 px-0">
              <Button
                variant="outline"
                size="sm"
                disabled={
                  !currentShow?.id ||
                  allocateCodes.isPending ||
                  !isOnline ||
                  allocationPermissionDisabled
                }
                title={fetchDisabledReason}
                onClick={() => allocateAndStoreCodes(10)}
              >
                {allocateCodes.isPending ? 'Fetching…' : 'Fetch 10 Exhibit IDs'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={
                  !currentShow?.id ||
                  allocateCodes.isPending ||
                  !isOnline ||
                  allocationPermissionDisabled
                }
                title={fetchDisabledReason}
                onClick={() => allocateAndStoreCodes(1)}
              >
                Fetch 1 Exhibit ID
              </Button>
            </CardFooter>
          </Card>
        </details>
      )}

      {showAllowsIntake && (
        <RecentCheckIns
          events={intake.checkedInExhibits}
          showId={currentShow.id}
          defaultCollapsed={true}
          listIdPrefix="status-recent-checkins"
          showSyncNowPrompt={showSyncNowPrompt}
          onSyncPending={handleSyncPending}
          syncDisabled={syncDisabled}
          syncDisabledReason={syncDisabledReason}
          getJobStatus={getRecentJobStatus}
          labelPrintShow={allowsLabelPrinting ? currentShow : undefined}
          onCopyExhibitId={showAllowsIntake ? handleRecentCopy : undefined}
          onOpenEntry={handleOpenRecentEntry}
        />
      )}

      <Dialog
        open={Boolean(replacingEntry)}
        onOpenChange={open => !open && !replacing && setReplacingEntry(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace rejected Exhibit ID</DialogTitle>
            <DialogDescription>
              The server rejected Exhibit ID {replacingEntry?.code} for this entrant. Sync will
              assign a new Exhibit ID and retain the old-to-new mapping. The rejected code will not
              return to the pool.
            </DialogDescription>
          </DialogHeader>
          <label className="flex gap-2 text-sm">
            <input
              type="checkbox"
              checked={replacementAcknowledged}
              onChange={event => setReplacementAcknowledged(event.target.checked)}
            />
            I will replace any printed or issued label with the new Exhibit ID before judging.
          </label>
          <DialogFooter>
            <Button variant="ghost" disabled={replacing} onClick={() => setReplacingEntry(null)}>
              Cancel
            </Button>
            <Button
              disabled={!replacementAcknowledged || replacing}
              onClick={async () => {
                if (!replacingEntry) return
                setReplacing(true)
                try {
                  await intake.replaceRejectedCode(replacingEntry.id)
                  setReplacingEntry(null)
                  toast.success(
                    'Ready to sync with a new Exhibit ID. Replace the old label after syncing.'
                  )
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : 'Unable to save replacement')
                } finally {
                  setReplacing(false)
                }
              }}
            >
              Prepare replacement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
